import { ContainerClient } from '@azure/storage-blob';
import {
  Box,
  Flex,
  Heading,
  IconButton,
  Input,
  List,
  ListIcon,
  Spinner,
  Tooltip,
  useColorMode,
  useToast,
} from '@chakra-ui/react';
import { translationAction, uploadAction } from 'actions';
import { Promise as BluebirdPromise } from 'bluebird';
import InputSearch from 'components/InputSearch';
import { FILE_EXTENSION_UPLOAD_ALLOWED } from 'containers/Upload/UploadInput';
import { MAX_CONCURRENT_REQUEST, MAXIMUM_SUMMARY_FILES_SELECT, TEMPORARY } from 'helpers/constant';
import { TRANSLATED_FILE_STATUS, TRANSLATION_FILE_STATUS, UPLOAD_FILES_STATUS } from 'helpers/enum';
import {
  formatBytes,
  formatBytestoKBytes,
  getFileExtensionFromFileName,
  removeQueryString,
  renderFileIcon,
} from 'helpers/file';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle } from 'react-icons/fa';
import { MdAttachFile, MdClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentService } from 'services/document/document.service';
import { FileStore } from 'types/actions/upload';
import { FileDocument } from 'types/reducers/document';
import { TranslationFileDocument } from 'types/reducers/translation';
import { getBlobContainer } from 'utils/blob';
import { v4 as uuidv4 } from 'uuid';

interface SelectFileProps {
  onOpenFile: (_file: FileDocument) => void;
}

const SelectFiles = ({ onOpenFile }: SelectFileProps) => {
  const { t } = useTranslation();
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const toast = useToast();
  const { colorMode } = useColorMode();

  const dispatch = useDispatch();
  const { current } = useSelector((store) => store.translation);
  const { stats, status, files, blobUrl } = useSelector((state) => state.upload);
  const isTranslating = useSelector(
    (state) =>
      state.translation.current.results?.some(
        (file) => file.status === TRANSLATED_FILE_STATUS.PENDING
      )
  );

  const isDark = colorMode === 'dark';
  const selectFilesCount = current.selectedFiles.length ?? 0;
  const isUploadingFiles = status === UPLOAD_FILES_STATUS.UPLOADING;
  const isPendingFileExist = current?.results?.some(
    (file) => file.status === TRANSLATED_FILE_STATUS.PENDING
  );
  const isDisableUpload = !current.task_id || isUploadingFiles || isTranslating;
  const isDisableSelect = Boolean(!current.task_id || isPendingFileExist || isTranslating);
  const isReachMaximumFilesSelect = selectFilesCount >= MAXIMUM_SUMMARY_FILES_SELECT;

  const searchFiles = async (value: string) => {
    return DocumentService.getListFiles({
      page: 0,
      size: 10,
      file_name: value,
      sort_field: 'created_at',
      is_ascending: 'false',
      status: 'PROCESSED',
    });
  };

  const showMaximumFileSelectWarning = () => {
    toast({
      title: `Maximum ${MAXIMUM_SUMMARY_FILES_SELECT} files selected!`,
      status: 'warning',
      position: 'bottom-right',
      isClosable: true,
    });
  };

  const onSelectFile = (newFile: FileDocument) => {
    if (isReachMaximumFilesSelect) {
      return showMaximumFileSelectWarning();
    }

    dispatch(
      translationAction.addSelectedFile({
        ...newFile,
        translation_file_status: TRANSLATION_FILE_STATUS.UPDATED,
      })
    );
  };

  const onRemoveFile = (
    e: React.MouseEvent<SVGElement, MouseEvent>,
    deleteFile: TranslationFileDocument
  ) => {
    e.stopPropagation();
    // Block remove file when translating
    if (isTranslating) {
      return;
    }
    dispatch(translationAction.removeSelectedFile(deleteFile));
  };

  const handleUpload = () => {
    if (isUploadingFiles || isPendingFileExist) {
      return;
    }
    uploadInputRef.current?.click();
  };

  const onChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReachMaximumFilesSelect) {
      return showMaximumFileSelectWarning();
    }
    const files = (e.target?.files ?? []) as File[];
    if (!FILE_EXTENSION_UPLOAD_ALLOWED.includes(getFileExtensionFromFileName(files[0]?.name))) {
      toast({
        title: 'File is not valid!',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
      return;
    }
    dispatch(uploadAction.startUpload(files));
  };

  const uploadFileToBlobStorage = async (containerClient: ContainerClient, file: File) => {
    const blobClient = containerClient.getBlockBlobClient(uuidv4() + '_' + file.name);

    const blobHTTPHeaders = { blobContentType: file.type };

    return blobClient
      .uploadData(file, {
        blockSize: 20 * 1024 * 1024,
        concurrency: 40,
        blobHTTPHeaders,
      })
      .then(() => {
        dispatch(uploadAction.uploadSuccess());
        return {
          url: removeQueryString(blobClient.url),
          name: file.name,
          size: formatBytestoKBytes(file.size, 2),
        };
      })
      .catch(() => {
        dispatch(uploadAction.uploadFail(file));
      });
  };

  useEffect(() => {
    if (status === UPLOAD_FILES_STATUS.UPLOADING && blobUrl && files && files?.length > 0) {
      const containerClient = getBlobContainer(blobUrl);
      for (const file of files) {
        dispatch(
          translationAction.addSelectedFile({
            id: uuidv4(),
            name: file.name,
            url: '',
            size: parseFloat(formatBytestoKBytes(file.size, 2)),
            state: '',
            created_by: '',
            created_date: '',
            org: '',
            translation_file_status: TRANSLATION_FILE_STATUS.IDLE,
          })
        );
      }
      toast({
        title: `Start uploading ${stats.total} file`,
        status: 'success',
        position: 'bottom-right',
        isClosable: true,
      });

      const uploadedFile: FileStore[] = [];
      BluebirdPromise.map(
        files,
        async (file) => {
          return uploadFileToBlobStorage(containerClient, file).then((file) => {
            if (file) {
              uploadedFile.push(file);
            }
          });
        },
        { concurrency: MAX_CONCURRENT_REQUEST }
      )
        .then(() => {
          dispatch(
            uploadAction.completeUploadFile({
              files: uploadedFile,
              task_id: current.type !== TEMPORARY ? current.task_id : undefined,
            })
          );
          dispatch(
            translationAction.updateSelectedFile({
              data: {
                url: uploadedFile[0]?.url,
                translation_file_status: TRANSLATION_FILE_STATUS.UPLOADED,
              },
              isTemporary: current.type === TEMPORARY,
            })
          );
          dispatch(uploadAction.completeUpload());
        })
        .catch((e: Error) => console.log(e));
    }
  }, [blobUrl, status, files]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status === UPLOAD_FILES_STATUS.FINISH) {
      if (uploadInputRef.current) uploadInputRef.current.value = '';
      if (stats.uploadSuccess > 0) {
        toast({
          title: `${stats.uploadSuccess}/${stats.total} file uploaded successfully`,
          status: 'success',
          position: 'bottom-right',
          isClosable: true,
        });
      }

      if (stats.uploadFail > 0) {
        toast({
          title: `${stats.uploadFail}/${stats.total} file uploaded failed`,
          status: 'error',
          position: 'bottom-right',
          isClosable: true,
        });
      }
      dispatch(uploadAction.resetUpload());
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex
      direction='column'
      h={'100%'}
      maxH='calc(100vh - 178px)'
      px={5}
      borderRight={isDark ? '1px' : ''}
      borderRadius={!isDark ? '20px' : ''}
      shadow='shadow_box'
    >
      <Box flex='1 1 0px'>
        <Heading as='h3' my={6} mr='1rem' color={'white.600'} fontWeight={'bold'} size='md'>
          {t('translator.selectFiles')}
        </Heading>
        <Flex gap={2} w='100%'>
          <Box flex='1'>
            <InputSearch<FileDocument>
              callback={searchFiles}
              onSelect={onSelectFile}
              disabled={isDisableSelect}
            />
          </Box>
          <Tooltip
            label={isUploadingFiles ? t('translator.uploading') : t('translator.uploadFiles')}
          >
            <IconButton
              fontSize={20}
              bg='bg_main_input'
              border='border_gray_solid'
              aria-label='Open menu'
              icon={isUploadingFiles ? <Spinner /> : <MdAttachFile />}
              isDisabled={isDisableSelect}
              onClick={handleUpload}
            />
          </Tooltip>
          <Input
            ref={uploadInputRef}
            display='none'
            accept={FILE_EXTENSION_UPLOAD_ALLOWED.join(', ')}
            isDisabled={isDisableUpload}
            onChange={onChangeFile}
            type='file'
          />
        </Flex>
        <List mt={5} spacing={1}>
          {current?.selectedFiles?.map((file) => (
            <Flex
              key={file.id}
              pos='relative'
              p={5}
              pe={12}
              bg={
                file.translation_file_status === TRANSLATION_FILE_STATUS.UPDATED
                  ? 'bg_conversation_item_select'
                  : 'bg_conversation_item'
              }
              borderRadius={4}
              shadow='rgba(0, 0, 0, 0.05) 0px 0px 0px 1px'
              _hover={{
                transform: 'scale(1.08)',
                border:
                  file.translation_file_status === TRANSLATION_FILE_STATUS.UPDATED
                    ? 'border_selected_box'
                    : 'border_dashed_box',
                boxShadow:
                  file.translation_file_status === TRANSLATION_FILE_STATUS.UPDATED
                    ? 'shadow_box'
                    : 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px',
              }}
              cursor='pointer'
              onClick={() => onOpenFile(file)}
            >
              <Flex justify='center' direction='column' mx={2} me={3}>
                <ListIcon
                  as={renderFileIcon(file.name).icon}
                  boxSize={6}
                  color={renderFileIcon(file.name).color}
                />
              </Flex>
              <Tooltip label={file.name} placement='top-start'>
                <Box maxW='calc(100% - 60px)'>
                  <Box
                    overflow='hidden'
                    fontSize={14}
                    fontWeight={600}
                    whiteSpace='nowrap'
                    textOverflow='ellipsis'
                  >
                    {file.name}
                  </Box>
                  <Box color='grey.400' fontSize={12}>
                    {formatBytes(file.size * 1024)}
                  </Box>
                </Box>
              </Tooltip>
              <Tooltip label={t('common.cancel')}>
                <Box pos='absolute' top='0' right='-1'>
                  <ListIcon
                    as={MdClose}
                    boxSize={4}
                    color='white.500'
                    _hover={{ opacity: '0.7' }}
                    onClick={(e) => onRemoveFile(e, file)}
                  />
                </Box>
              </Tooltip>
              <Box as='button' pos='absolute' right='1' bottom='3' flexDir='column'>
                {file.translation_file_status !== TRANSLATION_FILE_STATUS.UPDATED && (
                  <Tooltip label='Uploading...'>
                    <Box>
                      <Spinner me={3} />
                    </Box>
                  </Tooltip>
                )}
                {file.translation_file_status === TRANSLATION_FILE_STATUS.UPDATED && (
                  <Tooltip label={t('translator.fileIsSaved')}>
                    <Box>
                      <ListIcon as={FaCheckCircle} boxSize={7} color='bg_icon' />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Flex>
          ))}
        </List>
      </Box>
    </Flex>
  );
};

export default SelectFiles;
